/* eslint-disable complexity */
import { TerminImage } from '@/domain/termine/components/TerminImage';
import { Termin } from '@/domain/termine/shared/types/Termin';
import {
  getWiederholungStringFromTerminWiederholung,
  TerminWiederholung,
} from '@/domain/termine/shared/types/TerminWiederholung';
import { cn } from '@/lib/utils';
import {
  faArrowsRepeat,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { TerminInfo } from '@/domain/termine/features/Calendar/shared/TerminInfo/TerminInfo';
import { TerminDeleteDrawer } from '@/domain/termine/features/Calendar/List/Termin/TerminDeleteDrawer';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@e2n/ui';
import { Link, useParams } from 'react-router-dom';

/* eslint-disable max-lines-per-function */
export function ListTerminDrawer({ termin }: { termin: Termin }) {
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState<'details' | 'delete' | null>(
    null,
  );
  const { year, month } = useParams();

  function handleDeleteClose(deleted: boolean) {
    setOpenDrawer(deleted ? null : 'details');
  }

  const showRepeatIcon =
    termin.wiederholung !== TerminWiederholung.OHNE &&
    termin.wiederholung !== TerminWiederholung.TAEGLICH;

  const isDeleteDisabled =
    termin.arbeitszeiten.length > 0 ||
    termin.schichten.length > 0 ||
    termin?.hasHiddenSchichten ||
    termin?.hasHiddenArbeitszeiten;

  return (
    <>
      <Drawer
        open={openDrawer === 'details'}
        onOpenChange={(isOpen) => {
          if (!isOpen) setOpenDrawer(null);
        }}
        modal>
        <DrawerTrigger asChild>
          <div
            onClick={(e) => {
              e.preventDefault();
              setOpenDrawer('details');
            }}
            className={cn(
              'flex items-center p-2 text-sm cursor-pointer h-12',
              'text-neutrals-900 bg-neutrals-100 w-full rounded-lg',
              'shadow-xs hover:bg-neutrals-300 transition-colors gap-2',
            )}>
            <TerminImage
              bildUrl={termin.image?.url}
              bildName={termin.image?.name}
              markierung={termin.markierung}
              variant="list"
            />
            <div className="flex flex-col justify-center">
              <span className="flex truncate">{termin.titel}</span>
              <span className="text-xs text-neutrals-800 flex items-center gap-1 w-full">
                {termin.beginn && dayjs(termin.beginn).format('HH:mm')}
                {showRepeatIcon && (
                  <FontAwesomeIcon
                    icon={faArrowsRepeat}
                    aria-label={t('domain.termine.repeat', {
                      wiederholung: getWiederholungStringFromTerminWiederholung(
                        termin.wiederholung,
                      ).toLowerCase(),
                    })}
                    className="text-neutrals-700"
                  />
                )}
              </span>
            </div>
          </div>
        </DrawerTrigger>
        <DrawerContent className="z-60 rounded-t-2xl pb-6">
          <DrawerTitle className="sr-only">{termin.titel}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {termin.titel}
          </DrawerDescription>
          <div className="w-full px-4 flex flex-col">
            <TerminInfo termin={termin} />
            <div className="flex flex-col gap-3 pt-4 justify-between">
              <Button
                asChild
                size="sm"
                variant="default"
                onClick={() => setOpenDrawer(null)}>
                <Link
                  to={`/termine/uebersicht/${year}/${month}/edit/${termin.id}`}>
                  <span>{t('domain.termine.edit')}</span>
                </Link>
              </Button>
              <Button
                size="sm"
                variant="ghost-destructive"
                onClick={() => setOpenDrawer('delete')}
                disabled={isDeleteDisabled}>
                <span>{t('domain.termine.delete')}</span>
              </Button>
              {isDeleteDisabled && (
                <div className="text-xs text-neutrals-700">
                  <FontAwesomeIcon icon={faInfoCircle} />{' '}
                  {t('domain.termine.deleteWithHiddenArbeitsbereich')}
                </div>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      <TerminDeleteDrawer
        termin={termin}
        open={openDrawer === 'delete'}
        onClose={handleDeleteClose}
      />
    </>
  );
}
